import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { CheckCircle, Eye, HourglassLow, Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import styled from 'styled-components';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import RegistrationStatusMessage from '@/components/RegistrationStatus/RegistrationStatusMessage';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogCurrency,
    getCatalogIsCatalogOnly,
    getCatalogIsTimedPlus,
    getCatalogIsTimedPlusBiddingOpen,
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
    getRegistrationBidLimit,
    getRegistrationDeclined,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
type Props = {
    catalogId: number;
    className?: string;
};

const CatalogRegistrationMessages = ({ catalogId, className }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const limit = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const bidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const catalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));
    const isTimedPlusAuction = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const isTimedPlusAuctionOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));

    const showSendSellerMessageModalDeclined = React.useCallback(() => {
        const messageDetails = {
            catalogId,
            message: `${formatMessage({
                id: 'message_registration_declined',
            })}`,
            title,
        };

        const subject = `${formatMessage({
            id: 'registration_declined',
        })}: ${title}`;

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogId, dispatch, formatMessage, sellerName, sellerId, title]);

    return catalogOnly ? (
        <RegistrationStatusMessage
            alertType="pending"
            className={className}
            header={<FormattedMessage id="registration.preview_only" />}
            icon={Eye}
            message={<FormattedMessage id="registration.preview_only_description" />}
        />
    ) : declined ? (
        <RegistrationStatusMessage
            alertType="declined"
            className={className}
            header={<FormattedMessage id="registration_declined" />}
            icon={Warning}
            message={
                <>
                    <FormattedMessage id="auctioneer_has_declined_registration" />
                    <ContactAuctioneerLink onClick={showSendSellerMessageModalDeclined}>
                        <FormattedMessage id="contact_auctioneer" />
                    </ContactAuctioneerLink>
                </>
            }
        />
    ) : limit ? (
        <RegistrationStatusMessage
            alertType="approved"
            className={className}
            header={
                <FormattedMessage
                    id="bid_limit_approval"
                    values={{
                        limit: (
                            <StyledFormattedCurrency
                                key="catalog-registration-bid-limit"
                                convert
                                noFraction
                                sourceCurrency={currency}
                                value={bidLimit}
                            />
                        ),
                    }}
                />
            }
            icon={CheckCircle}
            justify
            message={
                <FormattedMessage
                    id={
                        isTimedPlusAuction && !isTimedPlusAuctionOpen
                            ? 'timedPlusAuctionPendingBidOnAnyItems'
                            : 'you_can_bid_on_any_of_the_items'
                    }
                />
            }
        />
    ) : approved ? (
        <RegistrationStatusMessage
            alertType="approved"
            className={className}
            header={<FormattedMessage id="approved_to_bid" />}
            icon={CheckCircle}
            message={
                <FormattedMessage
                    id={
                        isTimedPlusAuction && !isTimedPlusAuctionOpen
                            ? 'timedPlusAuctionPendingBidOnAnyItems'
                            : 'you_can_bid_on_any_of_the_items'
                    }
                />
            }
        />
    ) : pending ? (
        <RegistrationStatusMessage
            alertType="pending"
            className={className}
            header={<FormattedMessage id="registration_pending" />}
            icon={HourglassLow}
            message={<FormattedMessage id="well_send_you_an_email" />}
        />
    ) : null;
};

export default CatalogRegistrationMessages;

const ContactAuctioneerLink = styled(Link)`
    margin-left: 3px;
`;

const StyledFormattedCurrency = styled(FormattedCurrency)`
    justify-content: center;
`;
